import React, { forwardRef, useCallback, useMemo, useState } from 'react';
import _, { isEmpty } from "lodash";
import { Form } from 'antd';
import GenericMappingForm from '../GenericMappingForm';
import { FieldTypes } from 'utils/formUtils';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import { useFeatureFlag } from 'contexts/FeatureFlagContext';
import { uniqBy } from 'utils/lodashUtils';

export const assetTypes = [
  {
    label: 'VM',
    value: 'VIRTUAL_MACHINE'
  },
  {
    label: 'Issues',
    value: 'ISSUES'
  },
   {
    label: 'Container',
    value: 'CONTAINER'
  }, {
    label: 'Container Image',
    value: 'CONTAINER_IMAGE'
  },
  {
    label: 'Serverless',
    value: 'SERVERLESS'
  },
  {
    label: 'Cloud Configurations',
    value: 'CLOUD_CONFIGURATION'
  },
  {
    label: 'Data Findings',
    value: 'DATA_FINDINGS'
  },
  {
    label: "Network Exposure",
    value: "NETWORK_EXPOSURE"
  },
  {
    label: 'Application Endpoint Findings',
    value: 'ENDPOINT'
  },
  {
    label: 'Repository Branch Findings',
    value: 'REPOSITORY_BRANCH'
  },
  {
    label: 'Bucket',
    value: 'BUCKET',
  },
  {
    label: 'Virtual Machine Image',
    value: 'VIRTUAL_MACHINE_IMAGE',
  }
];

export const wizBucketAssetTypes = [
  {
    label: "Host Configuration Findings",
    value: "HOST_CONFIGURATION_FINDINGS"
  },
  {
    label: "Secret Findings",
    value: "SECRET_FINDINGS"
  },
  {
    label: "External Attack Surface",
    value: "EXTERNAL_ATTACK_SURFACE"
  },
  {
    label: "Cloud Configuration Findings",
    value: "CLOUD_CONFIGURATION_FINDINGS"
  }
];

const initialFormValues = [{
    product: undefined,
    subProduct: undefined,
    environment: undefined,
    assetType: undefined
  }];

const WizMappingForm = forwardRef(function WizMappingForm(props, ref){
    const [form] = Form.useForm();
    const featureFlags = useFeatureFlag();
    const isWizBucket = props.loginConfig?.bucketName;
    if(isWizBucket){
      initialFormValues[0].assetType = undefined;
    } else{
      initialFormValues[0].assetType = assetTypes.slice(0,2).map(type => type.value);
    }
    const [mappedProjectList] = useState(() => {
      // this state contains the already mapped application/site
      // This is stored because the project and version dropdowns are paginated
      // and if the option selected is not on 2st page, then the ID is displayed as this is how select works
      if (props.record) {
        const { projectId, projectName } = props.record;
        return {
          projects: projectId ? [{ id: projectId, name: projectName }] : [],
        }
      }
      return { projects: [] }
    })

    const [options,setOptions] = useState(mappedProjectList.projects);
    
    const setOptionsFromPagination = (option) => {
        setOptions(prev => uniqBy([...prev,...option], 'id'));
    }

    const fetchProjects = useCallback((params) => {
      const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId,...params });
      promise.then((response) => {
        if(mappedProjectList['projects'].length > 0 && !isEmpty(response.projects)){
          response.projects = [...response.projects, ...mappedProjectList['projects']];
        }
        setOptionsFromPagination(response.projects)
      });
      return { promise, cancel };
    }, [props.toolName, mappedProjectList, props.loginConfigId]);

    const getExtraFieldsData = useCallback((config) => {
        const { projectId, assetType } = config;
        const projectName = options.find(project => project.id === projectId)?.name;
        const wizBucketAssetTypeValue = wizBucketAssetTypes
            .filter(type => _.isUndefined(type.flagKey) || featureFlags[type.flagKey])
            .map(type => type.value);
        return ({
            projectId: isEmpty((projectId || "").trim()) ? undefined : projectId,
            projectName: projectName,
            assetType: isEmpty(assetType) ? (isWizBucket ? wizBucketAssetTypeValue.join() : initialFormValues[0].assetType.join()) : assetType.join()
    })
    }, [featureFlags, isWizBucket, options]);

    const parseExtraFieldsDataFromResponse = useCallback((config) => {
        const { assetType } = config;
        return {  assetType: assetType.split(',') };
      }, []);

    const extraFields = useMemo(() => [
      {
        name: "projectId",
        colSpan: 10,
        label: featureFlags.WIZ_PULL_ROOT_PROJECTS ? "Parent Project / Folder Name" :"Project Name",
        placeholder: "Leave blank to select all",
        type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
        props: {
            autoFocus: true,
            showValueOnload: false,
            api: fetchProjects,
            apiParams: { login_id: props.loginConfigId },
        }},
        ...(isWizBucket ? [
          {
            name: 'assetType',
            colSpan: '10',
            placeholder: 'Asset Type',
            type: FieldTypes.MULTI_SELECT,
            label: 'Asset Type',
            help: 'If not specified, then defaults to All',
            options: wizBucketAssetTypes.filter(asset => {
              if (_.isUndefined(asset.flagKey)) return true;
              return featureFlags[asset.flagKey];
            })
          }
        ] : [
          {
            name: 'assetType',
            colSpan: '10',
            placeholder: 'Asset Type',
            type: FieldTypes.MULTI_SELECT,
            label: 'Asset Type',
            help: 'If not specified, then defaults to "VM(virtual field)".',
            options: assetTypes.filter( asset => {
              if(_.isUndefined(asset.flagKey)) return true;
              return featureFlags[asset.flagKey];
            }),
        }])
    ], [featureFlags, fetchProjects, isWizBucket, props.loginConfigId])

    return (
        <GenericMappingForm 
            form={form}
            ref={ref}
            getExtraFieldsData={getExtraFieldsData}
            extraFields={extraFields}
            parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
            formDefaultValues={initialFormValues}
            newRowDefaultValue={initialFormValues[0]}
            {...props}
        />
    )
});

export default WizMappingForm;